import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { unitId as brandUnitId, actionId } from '@/annotations'
import { Button } from '@/components/Button'
import { Note } from '@/components/Note'
import { Field } from '@/components/Field'
import { Chip } from '@/components/Chip'
import { GridTable } from '@/components/GridTable'
import { WizardProse, WizardButtons, WizardSplit, WizardCard, WizardHeading } from '@/components/WizardParts'
import { DestinationWorkspacePicker } from './DestinationWorkspacePicker'
import { getApiClient, ApiProblemError } from '@/data/apiClient'
import { UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'
import { useActiveOrgId, useActiveWorkspaceId } from '@/data/store'
import type { components } from '@/api/generated/schema'
import type { Problem } from '@/data/apiClient'
import type { RestoreWizardConfig } from '@/layers/restore-wizard/types'

type SnapshotWorkspaceHook = components['schemas']['SnapshotWorkspaceHook']
type ProbePresence = components['schemas']['ProbePresence']

const PRESENCE_LABEL: Record<string, string> = { PRESENT: 'Present', ABSENT: 'Absent', NOT_CHECKED: 'Not checked' }
const PRESENCE_CHIP: Record<string, 'warning' | 'active' | 'pending'> = { PRESENT: 'warning', ABSENT: 'active', NOT_CHECKED: 'pending' }

function hookKey(hook: SnapshotWorkspaceHook): string {
  return `${hook.url}|${[...hook.events].sort().join(',')}`
}

/**
 * `wshook-wz-2` — `WORKSPACE_WEBHOOK`'s `destination` pane. Workspace-scoped, no repository
 * (`SnapshotWorkspaceHook` carries no `repo_id` at all — §7b family 2 omits it for workspace-scoped
 * types). **Secrets were never captured** (write-only on the platform) — `had_secret` is what lets
 * this pane say so without pretending to hold one. Same reported gap as `TargetPullRequestsPane`:
 * `RestoreInput` has no field to narrow which hooks are restored; every captured hook for the
 * workspace is recreated.
 *
 * **`panel`/`panel-header` added W166** — wraps the already-rendered table; no new content.
 *
 * **Presence chip recoloured (W172)** — was flat grey for every state. `ref-wz-2`'s own presence
 * probe (the same `ProbePresence` schema) draws `Absent` as `st-active`: absent at the destination
 * means nothing conflicts, the safe state to write into. `Present` is mapped to `warning` by the
 * inverse of that reasoning (not itself a direct example); `NOT_CHECKED` to `pending`.
 */
export function TargetWorkspaceHooksPane({
  config,
  heading,
  jobId,
  destinationWorkspaceName,
  onNext,
  onBack,
}: {
  config: RestoreWizardConfig
  heading: string
  jobId: string
  destinationWorkspaceName: string
  onNext: (args: { destinationWorkspaceId?: string; destinationLabel: string }) => void
  onBack: () => void
}) {
  const orgId = useActiveOrgId()!
  const workspaceId = useActiveWorkspaceId()!
  const [destination, setDestination] = useState<{ id: string; name: string }>({ id: workspaceId, name: destinationWorkspaceName })

  const hooks = useQuery({
    queryKey: ['snapshot-workspace-hooks', orgId, workspaceId, jobId],
    // Found live, 2026-09-25 (systemic audit after VHealth.tsx/VDrift.tsx's own fix) — see that
    // fix's own doc comment for the mount-time/org-switch race this guards against.
    enabled: Boolean(orgId && workspaceId),
    queryFn: async (): Promise<readonly SnapshotWorkspaceHook[]> => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/snapshots/{job_id}/workspace-hooks', {
        params: { path: { org_id: orgId, workspace_id: workspaceId, job_id: jobId } },
      })
      if (error) throw new ApiProblemError(error as Problem)
      return (data?.items ?? []) as unknown as readonly SnapshotWorkspaceHook[]
    },
  })

  const keys = (hooks.data ?? []).map(hookKey)

  const presence = useQuery({
    queryKey: ['workspace-hook-presence', orgId, workspaceId, keys],
    enabled: keys.length > 0,
    queryFn: async (): Promise<ProbePresence> => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/probes/workspace-hook', {
        params: { path: { org_id: orgId, workspace_id: workspaceId }, query: { hook: keys } },
      })
      if (error) throw new ApiProblemError(error as Problem)
      return data as unknown as ProbePresence
    },
  })

  const problem = hooks.error instanceof ApiProblemError ? hooks.error.problem : undefined

  return (
    <div data-scr={brandUnitId(config.unitIds.targets)} data-cmp="pane">
      <WizardSplit>
        <div>
          <WizardHeading>{heading}</WizardHeading>
          <WizardProse>Every captured workspace webhook is recreated. A hook's secret is never captured — it is write-only on the platform — so a recreated hook signs nothing until you set one.</WizardProse>
        </div>

        <WizardCard>
          <h4 className="m-0 mb-4 text-body-lg font-semibold">Destination and scope</h4>

          <Field label="Destination">
            <DestinationWorkspacePicker currentWorkspaceId={workspaceId} value={destination.id} onChange={(id, name) => setDestination({ id, name })} />
          </Field>

          <WizardButtons>
            <Button data-act={actionId(`nav.${config.unitIds.select}`)} ghost onClick={onBack}>
              Back
            </Button>
            <Button
              data-act={actionId(`nav.${config.unitIds.plan}`)}
              onClick={() => onNext({ destinationWorkspaceId: destination.id !== workspaceId ? destination.id : undefined, destinationLabel: destination.name })}
            >
              Next
            </Button>
          </WizardButtons>
        </WizardCard>
      </WizardSplit>

      {problem && (isUnexpectedRefusal(problem.refusal_kind) ? <UnexpectedRefusal problem={problem} /> : <Note>{problem.title}</Note>)}

      <div data-cmp="panel" className="bg-panel border border-bd rounded-2 mb-7">
        <div data-cmp="panel-header" className="p-5 border-b border-bd font-semibold text-body-lg">Workspace webhooks</div>
        <GridTable
          columns={[
            { key: 'url', header: 'URL', render: (hook) => <span className="font-mono">{hook.url}</span> },
            { key: 'events', header: 'Events', render: (hook) => hook.events.join(', ') },
            { key: 'secret', header: 'Secret', render: (hook) => (hook.had_secret ? 'Had a secret (not captured)' : 'None') },
            {
              key: 'presence',
              header: 'Presence',
              render: (hook) => {
                const item = presence.data?.items.find((i) => i.key === hookKey(hook))
                return <Chip variant={item ? (PRESENCE_CHIP[item.presence] ?? 'plain') : 'plain'}>{item ? PRESENCE_LABEL[item.presence] : '…'}</Chip>
              },
            },
          ]}
          rows={hooks.data ?? []}
          getRowKey={hookKey}
        />
      </div>
      <Note>{presence.data?.advisory_note ?? 'Each object is re-read as it is written; the report is what is true.'}</Note>
    </div>
  )
}
